import { ExternalLink } from 'lucide-react'
import { Button, Chip, Drawer } from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import { useProductCoverage } from '../api/queries'
import type {
  CoverageDemandLine,
  CoverageStatus,
  CoverageSupplyLine,
} from '../api/types'

export interface ProductCoverageDrawerProps {
  productId: number | null
  onClose: () => void
}

/**
 * Demand-vs-supply drilldown for one product (M11 audit Row 9 —
 * restoration of the legacy ProductCoveragePanel).
 *
 * Triggered from OrderDrawer's "View product coverage" action. The
 * parent swaps drawers (closes OrderDrawer when opening this one)
 * so only one drawer is on-screen at a time.
 *
 * Wide variant so demand + supply fit side-by-side; narrow viewports
 * still scroll since the Drawer is `overflow-auto`. Both link-heavy
 * columns (SO #, PO #) resolve to the Odoo web form via `odooUrl`.
 */
export function ProductCoverageDrawer({
  productId,
  onClose,
}: ProductCoverageDrawerProps) {
  const { data, isLoading, isError, refetch } = useProductCoverage(productId)

  return (
    <Drawer
      open={productId !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      size="wide"
      ident={data ? `#${data.product_id}` : undefined}
      title={data?.product_name ?? 'Loading…'}
      meta={
        data ? (
          <>
            <StatusChip status={data.summary.coverage_status} />
            <span className="opacity-40">·</span>
            <span>
              Demand <strong>{fmtQty(data.summary.total_demand_open)}</strong>
            </span>
            <span className="opacity-40">·</span>
            <span>
              Supply <strong>{fmtQty(data.summary.total_supply_open)}</strong>
            </span>
            <span className="opacity-40">·</span>
            <span
              className={
                data.summary.net_coverage >= 0 ? 'text-success-text' : 'text-danger-text'
              }
            >
              Net {data.summary.net_coverage >= 0 ? '+' : ''}
              {fmtQty(data.summary.net_coverage)}
            </span>
          </>
        ) : undefined
      }
      headerActions={
        data ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              window.open(odooUrl('product.product', data.product_id), '_blank')
            }
          >
            Open in Odoo
            <ExternalLink size={12} />
          </Button>
        ) : undefined
      }
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {productId === null ? null : isLoading ? (
        <div className="p-6 text-sm text-tertiary">Loading coverage…</div>
      ) : isError ? (
        <div className="flex flex-col gap-2 p-6">
          <p className="text-sm text-danger-text">
            Couldn&apos;t load coverage data.
          </p>
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : data ? (
        <CoverageBody demand={data.demand} supply={data.supply} />
      ) : null}
    </Drawer>
  )
}

function CoverageBody({
  demand,
  supply,
}: {
  demand: CoverageDemandLine[]
  supply: CoverageSupplyLine[]
}) {
  return (
    <div className="grid grid-cols-1 gap-5 p-4 lg:grid-cols-2">
      <DemandTable demand={demand} />
      <SupplyTable supply={supply} />
    </div>
  )
}

function DemandTable({ demand }: { demand: CoverageDemandLine[] }) {
  const totalOpen = demand.reduce((sum, d) => sum + d.qty_open, 0)
  return (
    <section>
      <SectionLabel tone="danger">
        Demand — Customer Orders ({demand.length})
      </SectionLabel>
      {demand.length === 0 ? (
        <EmptyRow>No open demand</EmptyRow>
      ) : (
        <table className="w-full border-collapse text-sm tabular-nums">
          <thead>
            <tr className="border-b border-border-default">
              <Th>Customer</Th>
              <Th>SO #</Th>
              <Th right>Qty open</Th>
              <Th>Ship</Th>
            </tr>
          </thead>
          <tbody>
            {demand.map((d, i) => (
              <tr key={`${d.so_id}-${i}`} className="border-b border-border-subtle">
                <Td className="truncate">{d.customer}</Td>
                <Td>
                  <a
                    href={odooUrl('sale.order', d.so_id)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-link hover:underline"
                  >
                    {d.so_number}
                  </a>
                </Td>
                <Td right>{fmtQty(d.qty_open)}</Td>
                <Td>{fmtDate(d.requested_ship_date)}</Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border-default">
              <Td colSpan={2} className="font-semibold">
                Total
              </Td>
              <Td right className="font-semibold">
                {fmtQty(totalOpen)}
              </Td>
              <Td />
            </tr>
          </tfoot>
        </table>
      )}
    </section>
  )
}

function SupplyTable({ supply }: { supply: CoverageSupplyLine[] }) {
  const totalOpen = supply.reduce((sum, s) => sum + s.qty_open, 0)
  return (
    <section>
      <SectionLabel tone="success">
        Supply — Vendor POs ({supply.length})
      </SectionLabel>
      {supply.length === 0 ? (
        <EmptyRow>No open supply</EmptyRow>
      ) : (
        <table className="w-full border-collapse text-sm tabular-nums">
          <thead>
            <tr className="border-b border-border-default">
              <Th>Vendor</Th>
              <Th>PO #</Th>
              <Th right>Qty open</Th>
              <Th>Expected</Th>
            </tr>
          </thead>
          <tbody>
            {supply.map((s, i) => (
              <tr key={`${s.po_id}-${i}`} className="border-b border-border-subtle">
                <Td className="truncate">{s.vendor}</Td>
                <Td>
                  <a
                    href={odooUrl('purchase.order', s.po_id)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-link hover:underline"
                  >
                    {s.po_number}
                  </a>
                </Td>
                <Td right>{fmtQty(s.qty_open)}</Td>
                <Td>{fmtDate(s.expected_receipt)}</Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border-default">
              <Td colSpan={2} className="font-semibold">
                Total
              </Td>
              <Td right className="font-semibold">
                {fmtQty(totalOpen)}
              </Td>
              <Td />
            </tr>
          </tfoot>
        </table>
      )}
    </section>
  )
}

function SectionLabel({
  children,
  tone,
}: {
  children: React.ReactNode
  tone: 'danger' | 'success'
}) {
  return (
    <div
      className={
        'mb-2 border-b border-border-subtle pb-1 text-role-section-label ' +
        (tone === 'danger' ? 'text-danger-text' : 'text-success-text')
      }
    >
      {children}
    </div>
  )
}

function StatusChip({ status }: { status: CoverageStatus }) {
  if (status === 'covered') return <Chip variant="success" size="xs">Covered</Chip>
  if (status === 'partial') return <Chip variant="warning" size="xs">Partial</Chip>
  return <Chip variant="danger" size="xs">Uncovered</Chip>
}

function Th({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={
        'py-1.5 pr-2 text-role-section-label ' + (right ? 'text-right' : 'text-left')
      }
    >
      {children}
    </th>
  )
}

function Td({
  children,
  right,
  colSpan,
  className,
}: {
  children?: React.ReactNode
  right?: boolean
  colSpan?: number
  className?: string
}) {
  const base =
    'py-1.5 pr-2 text-sm text-primary ' +
    (right ? 'text-right' : 'text-left') +
    (className ? ' ' + className : '')
  return (
    <td className={base} colSpan={colSpan}>
      {children}
    </td>
  )
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-sm border border-border-subtle bg-surface-sunk p-3 text-xs italic text-tertiary">
      {children}
    </div>
  )
}

function fmtQty(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—'
  return d.slice(0, 10)
}
